/**
 * Common utility behavior for enums.
 *
 * Usage (in an extending module):
 *
 * import Enum from "./Enum";
 * const myEnum = Object.create( Enum );
 * myEnum.FOO_KEY = "FOO_VALUE";
 * myEnum.BAR_KEY = "BAR_VALUE";
 * export default Object.freeze( myEnum );
 *
 * // Then, in a consuming module:
 * // import { MyEnum } from "@teaming/common-enums";
 * // MyEnum.keyFor( "FOO_VALUE" ) === "FOO_KEY";
 */
const Enum = {
	keys() {
		// only return keys for non-function (behavior) values
		return Object.keys( this )
			.filter( key => Object.prototype.toString.call( this[ key ] ) !== "[object Function]" );
	},

	values() {
		return this.keys()
			.map( key => this[ key ] );
	},

	pairs() {
		return this.keys().map( key => [
			key,
			this[ key ],
		] );
	},

	keyFor( value ) {
		const keys = Object.keys( this );
		for ( const key of keys ) {
			if ( this[ key ] === value ) {
				return key;
			}
		}
		return undefined;
	},

	includes( value ) {
		return this.values().includes( value );
	},

	toString() {
		return JSON.stringify( this );
	},

	/**
	 * @example
	 * const myEnum = Object.create( Enum );
	 * myEnum.FOO = "FOO1";
	 * myEnum.BAR = "BAR1";
	 * for ( key of myEnum ) {
	 *     console.info( key );
	 *     // FOO
	 *     // BAR
	 *     console.info( myEnum[ key ] );
	 *     // FOO1
	 *     // BAR1
	 * }
	 */
	[ Symbol.iterator ]() {
		const keys = this.keys();
		const last = Math.max( 0, keys.length - 1 );
		return {
			current: 0,
			last,
			next() {
				if ( this.current <= last ) {
					return {
						done: false,
						value: keys[ this.current++ ],
					};
				}
				return { done: true, };
			},
		};
	},
};

Enum.empty = Object.create( Enum );

Enum.create = properties => {
	const freshlyMintedEnum = Object.create( Enum );
	Object.assign( freshlyMintedEnum, properties );
	return Object.freeze( freshlyMintedEnum );
};

export default Enum;
